(ns app.domain.fee.formula
  (:require [clojure.string :as str]
            [potpuri.core :as p]))


(defn constant? [token]
  (re-matches #"^[0-9.]+$" token))

(defn operator? [token]
  (re-matches (re-pattern "^(?:<|>|=|!=|&&|\\|\\||<=|>=|\\+|\\-|\\*|\\/|\\^)$") token))

(defn paren? [token]
  (re-matches #"^[\(\)]{1}$" token))

(defn comma? [token]
  (= "," token))

(defn variable-name? [token]
  (re-matches #"^:[a-z0-9_]*$" token))

(defn variable? [token fee-variable-identifiers]
  (and (p/find-first fee-variable-identifiers #(= % token))
       (> (count token) 1)
       (variable-name? token)))

(def supported-boolean-operators ["<" ">" "=" "!=" "&&" "||" "<=" ">="])

(def supported-operators ["+" "-" "*" "/" "^"])

(def supported-math-functions ["min" "max" "abs" "round" "ceiling" "floor" "sqrt"])

(def supported-conditional-functions ["if"])

(def supported-functions+operators (vec (concat supported-math-functions
                                                supported-operators
                                                supported-conditional-functions)))

(defn function? [token]
  (->> token
       str/lower-case
       (contains? (set (into supported-math-functions supported-conditional-functions)))))

(defn- string-token? [token]
  ;; token is considered a string if it starts and ends with a quote
  (re-matches #"\".+\"" token))

(defn- boolean-token? [token]
  (and (not (str/blank? token))
       (or (= token "true")
           (= token "false"))))

(defn token->type [identifiers token]
  (cond
    (constant? token) :constant
    (operator? token) :operator
    (paren? token) :paren
    (comma? token) :comma
    (variable? token identifiers) :variable
    (function? token) :function
    (string-token? token) :string
    (boolean-token? token) :boolean
    :else :unknown))

(defn- string->number [s]
  #?(:clj  (Integer/parseInt s)
     :cljs (js/parseInt s)))

(defn- too-many-decimals-for-round?
  "Catches too big decimals for simple rounding formulas like ROUND(123/33, 11)
   but can't handle any 'complex' ones like ROUND(123/33, 11 + 3)"
  [formula]
  (let [pattern #"(?i)ROUND\s*\(\s*[^,]+,\s*(\d+)\s*\)"
        matches (re-seq pattern formula)]
    (some #(> (string->number (second %)) 10) matches)))

(defn number-touching-variable?
  "Checks for errors like `1 + 3:variable` or `3     :variable`"
  [formula]
  (re-find #"\d\s*:" formula))

(defn- has-unknown-token? [tokens]
  (->> tokens
       (some #{:unknown})
       boolean))

(defn- boolean-operator? [token]
  (->> supported-boolean-operators
       (some #{token})
       boolean))

(defn- add-operator [tokens currentWord char]
  (let [combined-boolean-operator (str (last tokens) char)]
    (if (boolean-operator? combined-boolean-operator)
      ["" (-> tokens
              butlast
              vec
              (conj currentWord combined-boolean-operator))]
      ["" (conj tokens currentWord char)])))

(defn tokenize [formula]
  (let [chars             (-> formula
                              (str/replace #"\s" "")
                              (str/split #""))
        operator-parts    #{"+" "-" "*" "/" "(" ")" "," "^" "<" ">" "=" "!" "&" "|"}
        ;; Parse the formula one character at a time, separating it into a vector of
        ;; operators, numbers and variable names (does not do validation, so any
        ;; character string goes)
        [lastWord tokens] (reduce (fn [[currentWord tokens] char]
                                    (if (contains? operator-parts char)
                                      (add-operator tokens currentWord char)
                                      [(str currentWord char) tokens]))
                                  ["" []]
                                  chars)]
    (->> (conj tokens lastWord)
         (remove str/blank?))))

(defn- validate-formula [formula types tr]
  (cond
    (number-touching-variable? formula)
    {:invalid-formula? true :formula-message (tr [:fees/invalid-formula-number-touching-var])}

    (has-unknown-token? types)
    {:invalid-formula? true :formula-message (tr [:fees/invalid-formula-unknown-tokens])}

    (too-many-decimals-for-round? formula)
    {:invalid-formula? true :formula-message (tr [:fees/invalid-formula-too-many-decimals-for-round])}

    :else
    {:invalid-formula? false :formula-message nil}))

(defn parse-formula
  "Given a formula, a list of fee variables and if the formula can be calculated on the backend,
  returns a map containing the formula in both intact and tokenized forms and an `invalid?` boolean.

  Parameters:
  `formula`        string, the formula as the user has entered it
  `fee-variables`  list of FeeVariables to cross-check the variables used in the formula against
  `server-status`  :valid, :invalid or :not-checked, was the formula calculable by EvalEx
  `server-message` string, a possible error returned by EvalEx"
  [formula fee-variables server-status server-message tr]
  (when formula
    (let [tokens                                     (tokenize formula)
          identifiers                                (map :fee-variable/identifier fee-variables)
          types                                      (map (partial token->type identifiers) tokens)
          {:keys [invalid-formula? formula-message]} (validate-formula formula types tr)]
      {:formula  formula
       :tokens   tokens
       :invalid? (or (= :invalid server-status) invalid-formula?)
       :message  (or server-message formula-message)})))


(defn first-variable
  "Given a formula string, returns the first variable-looking thing in it
  E.g. `1 + 3 * :area - :height - 2` will return `:area`"
  [formula]
  (-> formula
      tokenize
      (p/find-first variable-name?)))
